import {
  assessCharacterCombatReadiness,
  classifyCombatReadiness,
} from '../../utils/CharacterCombatReadiness';
import {
  collectCombatantSources,
  resolveAbilityScores,
  resolveCombatantArmorClass,
  resolveCombatantCurrentHp,
  resolveCombatantSpeedFeet,
  toFiniteNumber,
} from '../../utils/combat/CombatantNormalizer';

const PLACEHOLDER_ACTION_NAME = /^(?:action|attack|weapon(?: attack)?|spell|spell attack|unnamed spell|unknown spell)$/i;

const record = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const firstText = (...values) => values
  .map((value) => String(value ?? '').trim())
  .find(Boolean) || '';

const assetSources = (asset) => collectCombatantSources(
  asset,
  asset?.originalUnifiedEntity,
  asset?.canonicalData,
  asset?.customData,
);

export const resolveAssetDocument = (asset) => (
  record(asset?.originalUnifiedEntity)
  || record(asset?.canonicalData)
  || record(asset?.customData)
  || record(asset)
  || {}
);

export const resolveCampaignScope = (asset) => {
  for (const source of assetSources(asset)) {
    const externalSource = record(source.externalSource);
    const system = firstText(externalSource?.system).toLowerCase();
    if (!['gmc', 'gamemastercraft'].includes(system)) continue;
    return {
      campaignSpecific: true,
      campaignId: firstText(externalSource?.campaignId) || null,
      sourceEntityId: firstText(externalSource?.entityId, externalSource?.actorId, externalSource?.id) || null,
    };
  }
  return { campaignSpecific: false, campaignId: null, sourceEntityId: null };
};

const sourceSnapshot = (asset) => {
  for (const source of assetSources(asset)) {
    const snapshot = record(source?._meta?.sourceSnapshot);
    if (snapshot) return snapshot;
  }
  return null;
};

const hasVcsMechanicalReview = (asset) => assetSources(asset).some((source) => (
  Boolean(firstText(source?._meta?.vcsMechanicalReviewAt))
));

const resolveMaximumHitPoints = (subject) => {
  for (const source of collectCombatantSources(subject)) {
    const candidates = [
      source.maxHp,
      source.hpObj?.max,
      source.hitPoints?.maximum,
      source.hitPoints?.max,
      source.hit_points?.maximum,
      source.hit_points?.max,
      source.hit_points?.average,
      source.defense?.hp?.max,
      source.hp?.maximum,
      source.hp?.max,
      source.hp?.average,
      typeof source.hitPoints === 'number' ? source.hitPoints : null,
      typeof source.hit_points === 'number' ? source.hit_points : null,
      typeof source.hp === 'number' ? source.hp : null,
    ];
    for (const value of candidates) {
      const numeric = toFiniteNumber(value);
      if (numeric != null) return numeric;
    }
  }
  return null;
};

const namedStructuredActions = (subject) => {
  const actions = [];
  for (const source of collectCombatantSources(subject)) {
    const collections = [
      source.actions,
      source.bonusActions,
      source.reactions,
      source.legendaryActions,
      source.legendary?.options,
    ];
    for (const values of collections) {
      if (!Array.isArray(values)) continue;
      actions.push(...values.filter((entry) => {
        if (!record(entry)) return false;
        const name = firstText(entry.name, entry.label);
        return Boolean(name) && !PLACEHOLDER_ACTION_NAME.test(name);
      }));
    }
  }
  return actions;
};

const hasStructuredSpellOption = (subject) => {
  for (const source of collectCombatantSources(subject)) {
    const pools = [source.spells, source.spellcasting];
    for (const pool of pools) {
      if (!record(pool)) continue;
      const spells = [
        ...(Array.isArray(pool.known) ? pool.known : []),
        ...(Array.isArray(pool.prepared) ? pool.prepared : []),
        ...(Array.isArray(pool.atWill) ? pool.atWill : []),
        ...Object.values(record(pool.byLevel) || {}).flatMap((entries) => Array.isArray(entries) ? entries : []),
      ];
      if (spells.some((spell) => record(spell) && firstText(spell.name))) return true;
    }
  }
  return false;
};

export const assessMonsterCombatReadiness = (subject) => {
  const blockers = [];
  const sources = collectCombatantSources(subject);
  const name = sources.map((source) => firstText(source.name, source.identity?.name)).find(Boolean);
  const maximumHp = resolveMaximumHitPoints(subject);
  const currentHp = resolveCombatantCurrentHp(subject);
  const armorClass = resolveCombatantArmorClass(subject);
  const speed = resolveCombatantSpeedFeet(subject);
  const abilities = resolveAbilityScores(subject);

  if (!name) blockers.push('A combatant name is required.');
  if (maximumHp == null || maximumHp < 1) blockers.push('Maximum hit points must be explicitly set.');
  if (currentHp != null && (currentHp < 0 || (maximumHp != null && currentHp > maximumHp))) {
    blockers.push('Current hit points must be between 0 and maximum hit points.');
  }
  if (armorClass == null || armorClass < 0) blockers.push('Armor Class must be explicitly set.');
  if (speed == null || speed < 0) blockers.push('Movement speed must be explicitly set.');
  const missingAbilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma']
    .filter((ability) => toFiniteNumber(abilities[ability]) == null);
  if (missingAbilities.length) blockers.push(`Ability scores are missing: ${missingAbilities.join(', ')}.`);
  if (namedStructuredActions(subject).length === 0 && !hasStructuredSpellOption(subject)) {
    blockers.push('At least one named structured action or fully embedded spell is required.');
  }

  return { simulatorReady: blockers.length === 0, blockers, warnings: [] };
};

/**
 * The token library is a simulator entry point, so incomplete authoring records
 * never appear as selectable combatants. GMC projections are checked against
 * their source snapshot to avoid treating projection defaults as authored game
 * mechanics. date_of_change: 2026-08-01
 */
export const assessAssetCombatReadiness = (asset, type) => {
  const scope = resolveCampaignScope(asset);
  const subject = scope.campaignSpecific && sourceSnapshot(asset) && !hasVcsMechanicalReview(asset)
    ? sourceSnapshot(asset)
    : resolveAssetDocument(asset);
  if (String(type).toLowerCase() === 'character') {
    const assessment = assessCharacterCombatReadiness(subject);
    const classified = classifyCombatReadiness(assessment);
    return {
      simulatorReady: classified.simulatorReady,
      blockers: [...classified.errors, ...classified.blockingWarnings],
      warnings: classified.informationalWarnings,
    };
  }
  return assessMonsterCombatReadiness(subject);
};

export const filterCombatReadyAssets = (assets, {
  type,
  includeCampaignActors = true,
  activeCampaignId = null,
} = {}) => {
  const visible = [];
  let incompleteCount = 0;
  let excludedCampaignCount = 0;
  let otherCampaignCount = 0;
  for (const asset of Array.isArray(assets) ? assets : []) {
    const readiness = assessAssetCombatReadiness(asset, type);
    if (!readiness.simulatorReady) {
      incompleteCount += 1;
      continue;
    }
    const scope = resolveCampaignScope(asset);
    if (scope.campaignSpecific && !includeCampaignActors) {
      excludedCampaignCount += 1;
      continue;
    }
    if (
      scope.campaignSpecific
      && activeCampaignId
      && scope.campaignId !== String(activeCampaignId)
    ) {
      otherCampaignCount += 1;
      continue;
    }
    visible.push({ ...asset, campaignScope: scope, combatReadiness: readiness });
  }
  return { visible, incompleteCount, excludedCampaignCount, otherCampaignCount };
};
